import type { OfferItem } from "./types";
import { Card, Icon } from "./components";

/**
 * „Vor dem Senden zu klären" — die Wünsche des Kunden, für die es noch keinen Preis gibt.
 *
 * ── WARTESCHLANGE Punkt 76: was hier entfernt wurde, und warum ──────────────────────────────
 * Diese Karte trug bis zum 20.09.2026 zusätzlich die **Rückfragen** der KI: eine Liste offener
 * Fragen, ein Eingabefeld zum Beantworten, den Weg „Kundin fragen" mit Entwurf und echtem
 * Mailversand, einen Sammelversand „Alle offenen Fragen an Kundin senden", und eine Zählung, die
 * den Senden-Knopf mit beschriftete.
 *
 * **Antoine hat die ganze Funktion abbestellt** (20.09.2026, mit Bildschirmfoto): „Diese komplette
 * Funktionalität funktioniert nicht und sie braucht kein Mensch. […] **Nein, Code löschen.** Das
 * ist völlig überflüssig. Die Fragen sind auch größtenteils falsch etc. **Der Kunde schreibt ‚bei
 * Ihnen' und die KI fragt dann, wo der Event stattfinden soll.**" Und auf die Nachfrage, ob
 * wirklich alles weg soll: „Kann alles weg, ist alles überflüssig."
 *
 * Sein Beispiel trifft den Kern: die Fragen entstanden aus dem, was die KI im Anfragetext NICHT
 * verstanden hatte — und was sie nicht verstanden hatte, stand oft genug schon da. Eine Maschine,
 * die rät und dann fragt, kostet mehr Zeit, als sie spart.
 *
 * ── Was BLEIBT, und warum die Karte nicht ganz verschwindet ─────────────────────────────────
 * Die offenen **Wünsche** sind etwas anderes: Positionen, die der Kunde ausdrücklich genannt hat
 * und für die es im Katalog keine Entsprechung gibt. Sie sind kein Rateergebnis, sondern eine
 * Tatsache aus der Anfrage — und der Betrieb kann sie hier erledigen, indem er einen Preis
 * einträgt.
 *
 * Die Karte bleibt außerdem das Gerüst für **Punkt 77**: Antoine will an dieser Stelle den Hinweis
 * auf ausdrückliche Kundenwünsche, die im Angebot fehlen („Alles was der Kunde in der Anfrage
 * nachgefragt hat, soll berücksichtigt werden").
 *
 * ── Eine Entscheidung, die dokumentiert gehört ──────────────────────────────────────────────
 * Der Versandweg `POST /api/events/:id/offers/ask-customer` hat mit dem Rückbau seinen einzigen
 * Aufrufer verloren. Er BLEIBT trotzdem bestehen: er ist geprüft (`offer-ask-customer.test.ts`,
 * `kundin-fragen-mailversand.spec.ts`), und Punkt 77 ist sein wahrscheinlicher nächster Nutzer.
 * Ihn heute zu löschen und in wenigen Tagen neu zu bauen, wäre Verschwendung. **Vermerkt, damit
 * niemand ihn für einen wirkenden Mechanismus hält — er wartet, er wirkt nicht.**
 */
export function VorDemSendenKarte({ offeneWuensche }: { offeneWuensche: Pick<OfferItem, "id" | "name">[] }) {
  if (offeneWuensche.length === 0) return null;
  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-outline-variant/30 bg-surface-container-low px-4 py-2.5 dark:border-outline/30 dark:bg-surface-variant/10">
        <span className="flex items-center gap-2 text-beleg text-on-background dark:text-inverse-on-surface">
          <Icon name="price_change" className="text-[18px] text-secondary dark:text-[#b8ae9e]" />
          Vor dem Senden zu klären
          <span className="rounded-full bg-[#F0EAE0] px-2 py-0.5 text-beleg-mikro font-semibold text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]">
            {offeneWuensche.length} offen
          </span>
        </span>
        {/* Der Zaehler nimmt dem Block die Wirkung einer Sperre: es wird gezeigt, was offen ist,
            das Senden wird nicht verhindert. */}
        <span className="text-beleg-mikro text-on-surface-variant">Senden ist trotzdem möglich — MAESTRO fragt vorher nach.</span>
      </div>
      <ul className="divide-y divide-outline-variant/20 dark:divide-outline/20">
        {offeneWuensche.map((it) => (
          <li key={it.id} className="flex items-start gap-3 px-4 py-3" data-testid={`vor-dem-senden-wunsch-${it.id}`}>
            <Icon name="price_change" className="mt-0.5 shrink-0 text-[18px] text-secondary dark:text-[#b8ae9e]" />
            <div className="min-w-0">
              <div className="text-beleg-notiz text-on-background dark:text-inverse-on-surface">{it.name || "(ohne Namen)"}</div>
              {/*
                Punkt 32, Konzept § 6.4: Die Liste ist dieselbe geblieben (zubuchbar + 0,00 €),
                der Satz sagt jetzt aber, was der Zustand für den GAST bedeutet. „Trag einen Preis
                ein, dann zählt die Position mit" war seit Punkt 32 sogar falsch: eine zubuchbare
                Position zählt auch mit Preis nicht in den Angebotspreis — sie kommt obendrauf,
                wenn der Gast sie wählt. Ohne Preis kann er sie überhaupt nicht wählen, und genau
                das ist der Grund, warum sie hier steht.
              */}
              <div className="text-beleg-rechenweg text-on-surface-variant">
                Zubuchbar, aber ohne Preis — der Gast kann das so nicht auswählen. Trag einen Preis ein; MAESTRO merkt ihn
                sich für das nächste Mal.
              </div>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
